<template>
  <div
    class="products-infinite-scroll"
    :class="{'page-container': !withoutContainer}"
  >
    <header class="products-infinite-scroll__header">
      <h3 class="products-infinite-scroll__title">{{ title }} <template v-if="computedCountOfProducts">{{ `(${computedCountOfProducts})` }}</template></h3>
      <a
        v-if="link"
        @click.stop="handleLinkClick"
        :href="link"
        class="products-infinite-scroll__more">
        {{ computedLinkTitle }}
      </a>
    </header>
    <category-filter
      class="products-infinite-scroll__filters"
      without-container
      :data="filterData"
      @change-filter="handleChangeFilter"></category-filter>
    <products-simple-list
      @cardInViewportEvent="collectAnalyticsData($event)"
      :products="loadedProducts"
      :title-analytics="titleAnalytics"
      :shown-skeletons="shownSkeletons"
      :esputnik-block-id="esputnikBlockId"
      class="page-container__products products-infinite-scroll__category-products">
      <template #observer>
        <li
          ref="observer"
          v-intersect="{
            callback: isVisible
          }"
        ></li>
      </template>
    </products-simple-list>
  </div>
</template>

<script>
import api from '@/api'

import { transliteration } from '@/mixins/transliteration'

import CategoryFilter from '@/components/product/product-block/CategoryFilter'
import ProductsSimpleList from '@/components/product/product-block/ProductsSimpleList'
import { GoogleEnhancedEcommerce, GoogleTagManagerService } from '@/services/AnalyticService'

export default {
  name: 'ProductsInfiniteScroll',
  mixins: [transliteration],
  components: {
    ProductsSimpleList,
    CategoryFilter
  },
  props: {
    id: {
      type: [String, Number],
      required: true
    },
    title: {
      type: String,
      default: ''
    },
    titleAnalytics: {
      type: String,
      default: ''
    },
    link: {
      type: String,
      default: ''
    },
    linkTitle: {
      type: String,
      default: ''
    },
    products: {
      type: Array,
      default: () => []
    },
    linkClickPrevent: {
      type: Boolean,
      default: false
    },
    showFilter: {
      type: Boolean,
      default: false
    },
    filterData: {
      type: Array,
      default: () => []
    },
    dataForFixedBlock: {
      type: Object,
      default: () => {}
    },
    withoutContainer: {
      type: Boolean,
      default: false
    },
    esputnikBlockId: String
  },
  data () {
    return {
      isVisibleObserver: false,
      loadedInfo: this.dataForFixedBlock,
      loadedProducts: this.products,
      isLoading: false,
      categoryId: null,
      shownSkeletons: 0,
      collectedAnalyticsData: []
    }
  },
  computed: {
    computedLinkTitle () {
      return this.linkTitle || this.$t('see_all')
    },
    computedCountOfProducts () {
      return this.loadedInfo?.['goods_all'] ?? null
    }
  },
  methods: {
    async sendAnalyticsData () {
      if (GoogleEnhancedEcommerce.isEnabled) {
        if (this.collectedAnalyticsData.length) {
          await GoogleEnhancedEcommerce.viewItemList({
            analyticsData: this.collectedAnalyticsData,
            listId: this.id,
            listName: this.titleAnalytics
          })
          this.collectedAnalyticsData = []
        }
      } else {
        if (this.collectedAnalyticsData.length) {
          await GoogleTagManagerService.viewItemListAnalytics({
            analyticsData: this.collectedAnalyticsData,
            listId: this.id,
            listName: this.titleAnalytics
          })
          this.collectedAnalyticsData = []
        }
      }
    },
    collectAnalyticsData (el) {
      this.collectedAnalyticsData.push(el)
    },
    loadMoreProducts () {
      this.isLoading = true

      api.blocks.getFixedBlockProducts({
        id: this.id,
        offset: this.loadedInfo?.offset,
        categoryId: this.categoryId
      }).then(res => {
        this.isLoading = false

        if (res.success) {
          this.loadedInfo = res
          this.loadedProducts = [...this.loadedProducts, ...res.data]
        }
      })
    },
    handleChangeFilter (e) {
      const { id } = e

      this.categoryId = id
    },
    handleLinkClick (e) {
      if (this.linkClickPrevent) {
        e.preventDefault()
        this.$emit('link-click')
      }
    },
    isVisible (isVisible) {
      this.isVisibleObserver = isVisible
    }
  },
  watch: {
    collectedAnalyticsData () {
      setTimeout(() => {
        this.sendAnalyticsData()
      }, 1000)
    },
    categoryId () {
      this.loadedInfo = {}
      this.loadedProducts = []
      this.loadMoreProducts()
    },
    async 'loadedInfo.offset' () {
      await this.$nextTick()
      const { top, bottom } = this.$refs.observer.getBoundingClientRect()

      if (
        top >= 0 &&
        bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        this.loadedInfo.goods_left &&
        !this.isLoading &&
        this.isVisibleObserver
      ) {
        this.loadMoreProducts()
      }
    },
    isVisibleObserver (value) {
      if (value && !this.isLoading && this.loadedInfo.goods_left) {
        this.loadMoreProducts()
      }
    },
    isLoading (value) {
      if (value) {
        this.shownSkeletons = 10
      } else {
        this.shownSkeletons = 0
      }
    }
  }
}
</script>

<style scoped lang="sass">
.page-container__products,
.category-products-wrap__item
  margin-bottom: 0

.category-products__card--skeleton-mobile
  padding: 15px 10px
  display: block

  +mmin($tablet)
    display: none

.category-products__card--skeleton-desktop
  display: none

  +mmin($tablet)
    display: block

.category-products__card--observer
  width: 0
  height: 0

.products-infinite-scroll__header
  display: flex
  flex-flow: row wrap
  justify-content: space-between
  align-items: baseline
  margin: 0 0 15px

  +mmin($tablet)
    justify-content: flex-start
    margin: 0 0 30px

.products-infinite-scroll__title
  font-size: 18px
  font-weight: 500
  padding: 0
  margin: 0

  +mmin($tablet)
    font-size: 24px

.products-infinite-scroll__more
  color: $main-color
  font-size: 14px
  font-weight: 400 //Rubik 300

  +mmin($tablet)
    font-size: 16px
    margin-left: 20px

.category-products__card--btn
  width: 100%
  border: none!important
  align-self: center
  padding: 20px 0 0
  margin: 0 -1px
  background-color: $white-text-color

  button
    margin: 0 auto

.products-infinite-scroll__filters
  margin-bottom: 20px

</style>
